/**
 * <CancelAlternativeCard> — l'option proposée avant de partir (organism).
 *
 * La carte de rétention de la deuxième étape : un picto, ce qu'on propose, pourquoi, et une ou
 * deux façons de le demander. Le view-model choisit l'alternative sur le motif ; la carte ne
 * connaît aucun motif et n'en déduit rien.
 *
 * **Deux actions au maximum** : une étape pose une question et propose au plus deux réponses. La
 * deuxième passe en contour pour qu'un seul bouton soit plein.
 *
 * Composition de `<Card>` + `<IconBubble>` + `<Text>` + `<Button>`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type UICancelAlternative } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"

/** Diamètre de la pastille de tête — celle des tuiles d'action de l'accueil. */
const BUBBLE_SIZE = 44

export type CancelAlternativeCardProps = {
    alternative: UICancelAlternative
    /** Remonte la clé de l'action pressée — la page sait ce qu'elle ouvre. */
    onAction: (key: string) => void
    testID?: string
}

export function CancelAlternativeCard(props: CancelAlternativeCardProps) {
    const { alternative, onAction, testID } = props
    const { iconName, title, pitch, actions } = alternative

    return (
        <Card testID={testID} background="primary-soft" p="4">
            <Stack gap="3">
                <Row gap="3" align="center">
                    <IconBubble iconName={iconName} size={BUBBLE_SIZE} color="surface" iconColor="primary" />
                    <Text size="text-lg" weight="bold" color="primary" style={styles.title}>
                        {title}
                    </Text>
                </Row>
                <Text size="text-base" color="text-primary">
                    {pitch}
                </Text>
                <Stack gap="2">
                    {actions.map((action) => (
                        <Button
                            key={action.key}
                            label={action.label}
                            iconName={action.iconName}
                            variant={action.variant ?? "solid"}
                            fullWidth
                            onPress={() => onAction(action.key)}
                        />
                    ))}
                </Stack>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    // Le titre passe à la ligne plutôt que de pousser la pastille hors de la carte.
    title: { flexShrink: 1 },
}))
